import { hubBody, hubRequest } from "./client";
export type AppManifest = {
  name: string;
  description: string;
  version: string;
  icon: string;
  tools: string[];
  csp: { connectDomains: string[]; resourceDomains: string[] };
  permissions: ("camera" | "microphone" | "geolocation" | "clipboard-write")[];
};
export type AppSummary = {
  id: string;
  name: string;
  description: string;
  icon?: string;
  ownerId: string;
  ownerName: string;
  status: "private_active" | "company_active" | "pending_review" | "disabled";
  privateCurrentVersion?: number;
  sharedApprovedVersion: number | null;
  version: number;
  contentHash: string;
  manifest: AppManifest;
  createdAt: number;
  approval?:
    | { id: string; status: string; method: string; version: number }
    | null;
};
export type AppFinding = { code: string; message: string; line?: number };
export type AppScan = {
  valid: boolean;
  errors: AppFinding[];
  warnings: AppFinding[];
  size: number;
  contentHash?: string;
  scopeHash?: string;
  manifest?: AppManifest;
};
export type AppPackage = { id?: string; html: string; manifest: AppManifest };
export type AppVersion = {
  version: number;
  contentHash: string;
  scopeHash: string;
  createdAt: number;
  scan: { warnings: AppFinding[] };
  manifest: AppManifest;
};
export type AppLaunch = {
  appId: string;
  version: number;
  contentHash: string;
  html: string;
  manifest: AppManifest;
  proxyUrl: string;
  sandboxOrigin: string;
  hostOrigin: string;
  csp: AppManifest["csp"];
  effectiveCsp: AppManifest["csp"];
  effectivePermissions: AppManifest["permissions"];
  permissionStatus: "approved" | "private-restricted";
  contentSecurityPolicy: string;
  sandbox: "allow-scripts";
  approvedTools: string[];
  ownerName: string;
  sharedOnly?: boolean;
};
export type AppTool = {
  name: string;
  title?: string;
  description?: string;
  sensitivity: "L0" | "L1" | "L2";
  readOnly: boolean;
  inputSchema?: Record<string, unknown>;
};
export type AppOverview = {
  apps: AppSummary[];
  usage: {
    appId: string;
    personId: string;
    personName: string;
    tool: string;
    calls: number;
    bytes: number;
    errors: number;
  }[];
  notifications: {
    id: string;
    appId: string;
    personId: string;
    attempts: number;
    sentAt: number | null;
    lastError: string | null;
  }[];
};
const path = (id: string) => `/apps/${encodeURIComponent(id)}`;
export const listApps = () =>
  hubRequest<{ mine: AppSummary[]; shared: AppSummary[] }>("/apps");
export const getApp = (
  id: string,
  signal?: AbortSignal,
  sharedOnly?: boolean,
) =>
  hubRequest<AppSummary>(
    path(id),
    { signal },
    sharedOnly === true ? { sharedOnly: true } : undefined,
  );
export const validateApp = (pkg: AppPackage) =>
  hubRequest<AppScan>("/apps/validate", hubBody(pkg));
export const publishApp = (pkg: AppPackage) =>
  hubRequest<AppSummary>("/apps/publish", hubBody(pkg));
export const listAppVersions = (id: string) =>
  hubRequest<{ items: AppVersion[] }>(`${path(id)}/versions`).then((r) =>
    r.items
  );
export const rollbackApp = (id: string, version: number) =>
  hubRequest<AppSummary>(`${path(id)}/rollback`, hubBody({ version }));
export const unpublishApp = (id: string) =>
  hubRequest<AppSummary>(`${path(id)}/unpublish`, hubBody({}));
export const shareApp = (id: string) =>
  hubRequest<AppSummary>(`${path(id)}/share`, hubBody({}));
export const approveApp = (app: AppSummary, approve: boolean) =>
  hubRequest<AppSummary>(
    `${path(app.id)}/approve`,
    hubBody({
      version: app.approval?.version ?? app.version,
      contentHash: app.contentHash,
      approve,
    }),
  );
export const launchApp = (
  id: string,
  signal?: AbortSignal,
  sharedOnly?: boolean,
) =>
  hubRequest<AppLaunch>(`${path(id)}/launch`, {
    ...hubBody(sharedOnly === true ? { sharedOnly: true } : {}),
    signal,
  });
/** Hub 返回统一数据 envelope；MCP Apps SDK 收标准 CallToolResult。 */
export const callAppTool = async (
  launch: AppLaunch,
  name: string,
  args: Record<string, unknown>,
  signal?: AbortSignal,
) => {
  const envelope = await hubRequest<Record<string, unknown>>("/app-bridge", {
    ...hubBody({
      appId: launch.appId,
      version: launch.version,
      contentHash: launch.contentHash,
      ...(launch.sharedOnly === true ? { sharedOnly: true } : {}),
      name,
      arguments: args,
    }),
    signal,
  });
  return {
    content: [{ type: "text" as const, text: JSON.stringify(envelope) }],
    structuredContent: envelope,
  };
};
export const getAppsOverview = () =>
  hubRequest<AppOverview>("/apps/admin/overview");
/** 与 Hub open/catalog.ts 的 DATA_TOOLS 一致；不按内部 operation 前缀分类。 */
export const APP_DATA_TOOL_NAMES = [
  "me",
  "tasks_mine",
  "tasks_query",
  "focus_today",
  "calendar_range",
  "projects_list",
  "project_get",
  "okr_mine",
  "okr_company",
  "approvals_pending",
  "inspiration_feed",
  "inspiration_must_read",
  "inbox_items",
  "ai_actions_recent",
  "decisions_list",
  "crm_overview",
  "kb_search",
] as const;
const appDataNames = new Set<string>(APP_DATA_TOOL_NAMES);
export const isAppDataTool = (tool: AppTool) =>
  appDataNames.has(tool.name) && tool.readOnly === true &&
  ["L0", "L1", "L2"].includes(tool.sensitivity);
export const getAppCatalog = () =>
  hubRequest<{ tools: AppTool[] }>("/open/catalog").then((r) =>
    r.tools.filter(isAppDataTool)
  );
type Widget = {
  id: string;
  type: string;
  x: number;
  y: number;
  w: number;
  h: number;
  config: Record<string, unknown>;
};
type HomeState = {
  version: number;
  layout: { schema: "home/1"; cols: 12; widgets: Widget[] };
  widgets: {
    type: string;
    minW: number;
    maxW: number;
    minH: number;
    maxH: number;
  }[];
};
/** 追加真实布局，版本冲突由服务端拒绝，绝不覆盖并发修改。 */
export async function addAppToHome(id: string) {
  const state = await hubRequest<HomeState>("/home");
  const type = `app:${id}`,
    descriptor = state.widgets.find((w) => w.type === type);
  if (!descriptor) throw new Error("此应用当前不可用，刷新后重试");
  if (state.layout.widgets.some((w) => w.type === type)) {
    throw new Error("此应用已在个人主页中");
  }
  if (state.layout.widgets.length >= 24) {
    throw new Error("个人主页已满，请先移除一个组件");
  }
  const widget: Widget = {
    id: `app-${crypto.randomUUID()}`,
    type,
    x: 0,
    y: Math.max(0, ...state.layout.widgets.map((w) => w.y + w.h)),
    w: Math.min(descriptor.maxW, Math.max(descriptor.minW, 6)),
    h: Math.min(descriptor.maxH, Math.max(descriptor.minH, 6)),
    config: {},
  };
  const draft = await hubRequest<{ draftId: string; expectedVersion: number }>(
    "/home/validate",
    hubBody({
      layout: { ...state.layout, widgets: [...state.layout.widgets, widget] },
      expectedVersion: state.version,
    }),
  );
  return hubRequest<HomeState>(
    "/home/apply",
    hubBody({ draftId: draft.draftId, expectedVersion: state.version }),
  );
}
